import { type FC } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { LoadingButton } from 'base-ui'

import {
  FriendlyCaptchaChallenge,
  useHookFormFriendlyCaptchaFieldControls,
} from '../../../friendly-captcha'
import { type DirectContactFormSubmitStatus } from '../../hooks/useDirectContactFormSubmit'
import { CONTACT_FORM_SUBJECT_KEY } from '../../model/directContactFormSubjectCatalog'
import { type ResolvedContactFormSubject } from '../../model/directContactFormSubjectPolicySelectors'
import { DirectContactFormTextInfoSection } from '../DirectContactFormTextInfoSection'

import { useDirectContactFormHookFormDialogAuthentication } from './DirectContactFormHookFormDialogContext'
import { type DirectContactFormHookFormDialogValues } from './DirectContactFormHookFormDialogModel'

export const DirectContactFormHookFormDialogSubmitFields: FC<{
  isMutating: boolean
  resolvedSubject: ResolvedContactFormSubject
  submitStatus: DirectContactFormSubmitStatus
}> = ({ isMutating, resolvedSubject, submitStatus }) => {
  const { isLoggedIn, isLoginModalOpen } =
    useDirectContactFormHookFormDialogAuthentication()
  const { control, register, setValue, watch } =
    useFormContext<DirectContactFormHookFormDialogValues>()
  const [friendlyCaptchaSolution, orderId] = useWatch({
    control,
    name: ['fcSolution', 'orderId'],
  })
  const isOrderNumberMissing =
    resolvedSubject.policy.orderNumberRequirement === 'required' && !orderId
  const shouldRequireLogin =
    resolvedSubject.key === CONTACT_FORM_SUBJECT_KEY.DOCTOR_ORDER ||
    resolvedSubject.key === CONTACT_FORM_SUBJECT_KEY.COPAYMENT_EXEMPTION
  const isSubmitDisabled =
    !friendlyCaptchaSolution ||
    isOrderNumberMissing ||
    isMutating ||
    (!isLoggedIn && shouldRequireLogin)
  const shouldRenderCaptcha =
    submitStatus !== 'authenticationError' || !isLoginModalOpen
  const { onCaptchaDone } =
    useHookFormFriendlyCaptchaFieldControls<DirectContactFormHookFormDialogValues>(
      {
        fieldName: 'fcSolution',
        register,
        setValue,
        watch,
      },
    )

  return (
    <>
      <DirectContactFormTextInfoSection />
      {shouldRenderCaptcha ? (
        <FriendlyCaptchaChallenge
          doneCallback={onCaptchaDone}
          startMode="auto"
          whenThisPropChangesResetCaptcha={submitStatus !== 'idle'}
        />
      ) : null}
      <LoadingButton
        className="w-full"
        data-qa-id="contact-form-submit-button"
        disabled={isSubmitDisabled}
        loading={isMutating}
        size="large"
        type="submit"
      >
        <FormattedMessage id="product.consultation.button.submitMessage" />
      </LoadingButton>
    </>
  )
}
